import { useCallback, useEffect } from "react";
import { useBreakpoints } from "../styles/styleUtils";
import useClientContext, { setCurrentEntry, updateGigyaProfile } from "./useClientContext";
import useSession from "./useSession";

/**
 * Hook that provides methods to interact with the gigya window instance.
 * @example
 * import useGigya from "@hooks/useGigya";
 * ...
 * const { showAuthModal, logout } = useGigya();
 * const { initialAuthCheck } = useGigya();
 * const { profile } = useGigya();
 */
export default function useGigya() {
  const [{ gigyaProfile }, dispatch] = useClientContext();
  const { remove: removeSession } = useSession();
  const { isMobile } = useBreakpoints();

  /**
   * *Required Gigya callback methods that need to be on the window.
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      var jQuery = window.jQuery;

      /**
       * Checkbox registration function
       */
      window.setHiddenGigyaFields = function (cb) {
        jQuery(".chkPartnerOffers").attr("checked", cb.checked);
        jQuery(".chkEventsAndProducts").attr("checked", cb.checked);
      };

      window.enableSubmitButton = function (enable) {
        var button = jQuery(
          "div#gigya-register-screen input.gigya-input-submit[type=button]",
        ).first();

        if (enable) {
          button.css("opacity", "1").css("pointer-events", "auto");
        } else {
          button.css("opacity", ".5").css("pointer-events", "none");
        }
      };

      /**
       * Function to validate new registration emails
       */
      window.onSubmitButtonClicked = function () {
        var email = jQuery("input.gigya-valid[name='email']").first().val();

        if (email) {
          window.enableSubmitButton(false);
          var apikey = "jsonp_3cc77578c33b8ab59b755ec36a1046421c0008722dd9e6af2ca7052c91ac2882";

          jQuery.ajax({
            type: "GET",
            url: "https://api.kickbox.com/v2/verify",
            data: { email: email, apikey: apikey },
            async: true,
            jsonpCallback: "kickboxCB",
            contentType: "application/json",
            dataType: "jsonp",
            timeout: 15000,
            error: function (xhr, ajaxOptions, thrownError) {
              console.log("There was an error calling the Kickbox API!");
              console.log(xhr);
              console.log(ajaxOptions);
              console.log(thrownError);
              if (ajaxOptions == "timeout") {
                jQuery("div#gigya-register-screen span.gigya-error-msg[data-bound-to='email']")
                  .first()
                  .addClass("gigya-error-msg-active")
                  .html("Can't validate e-mail address, please try again later");
              }
            },
          });
        }
      };

      window.kickboxCB = function (data) {
        console.log("kickbox callback...");
        console.log(data);
        window.enableSubmitButton(true);
        if (data.result == "deliverable" || data.result == "risky" || data.result == "unknown") {
          jQuery("div#gigya-register-screen form.gigya-register-form input[type=submit]")
            .first()
            .click();
        } else {
          jQuery("div#gigya-register-screen span.gigya-error-msg[data-bound-to='email']")
            .first()
            .addClass("gigya-error-msg-active")
            .addClass("gigya-error-code-400006")
            .html("E-mail address is invalid.");
        }
      };
    }
  }, []);

  const formatGigyaProfile = (gigyaResponse) => ({
    return_entries: { chase: true },
    chase: true,
    return_user: true,
    gigya_id: gigyaResponse.UID || "",
    signature: gigyaResponse.UIDSignature || "",
    timestamp: gigyaResponse.signatureTimestamp || "",
    first_name: gigyaResponse.profile?.firstName || "",
    last_name: gigyaResponse.profile?.lastName || "",
    email: gigyaResponse.profile?.email || "",
  });
  /**
   * Gets their gigya account and sets gigyaProfile.
   */
  const getGigyaAccount = useCallback(
    () =>
      new Promise((resolve) => {
        if (typeof window === "undefined" || !window.gigya) {
          throw new Error("window.gigya is not defined");
        }

        if (gigyaProfile) {
          resolve(gigyaProfile);
        }

        window.gigya.accounts.getAccountInfo({
          callback: function (res) {
            // console.log(">>> gigya.getAccountInfo res", res);
            if (res.status != "OK" || res.errorCode) resolve(gigyaProfile);
            resolve(formatGigyaProfile(res));
          },
          context: "fantasy",
          include: "profile, data",
          extraProfileFields: "username, zip",
        });
      }).then((res) => {
        if (res) {
          dispatch(updateGigyaProfile(res));
        }
        return res;
      }),
    [gigyaProfile, dispatch],
  );

  function showAuthModal() {
    if (typeof window === "undefined" || !window.gigya) {
      throw new Error("window.gigya is not defined");
    }

    window.gigya.accounts.showScreenSet({
      screenSet: isMobile
        ? "Lite-Registration-en_us-Mobile-RegistrationLogin"
        : "Lite-Registration-en_us-RegistrationLogin",
      mobileScreenSet: "Lite-Registration-en_us-Mobile-RegistrationLogin",
      onAfterSubmit(data) {
        // console.log(">>> showScreenset | afterSubmit data", data);
        if (data.response?.UID) {
          const formattedProfile = formatGigyaProfile(data.response);
          dispatch(updateGigyaProfile(formattedProfile));
        } else {
          getGigyaAccount();
        }
      },
      authFlow: "redirect",
    });
  }

  function logout() {
    if (typeof window === "undefined" || !window.gigya) {
      throw new Error("window.gigya is not defined");
    }

    window.gigya.accounts.logout();
    dispatch(updateGigyaProfile(null));
    dispatch(setCurrentEntry(null));
    removeSession();
  }

  return { showAuthModal, getGigyaAccount, logout };
}
